import '../index.scss'
import names from '../names.json'
import Input from '../../../components/core/input'
import {FieldArray, Formik} from 'formik'
import {ImgButton} from '../../../components/core/button'
import CustomSelect from '../../../components/core/customSelect'
import {useEffect, useState} from 'react'
import {
    getAllServices,
    getAllServicesEnabledResources,
    setResources
} from '../../../stores/resources'
import {getTeams} from '../../../stores/teams'
import {useDispatch, useSelector} from 'react-redux'
import {
    CirclePlusIcon,
    DragIcon,
    PlusIcon,
    SecondaryTrashIcon,
    TrashIcon
} from '../../../icons'
import Textarea from '../../../components/core/textarea'
import {structureFormRef} from '../refs'
import {statuspageStructureSchema} from '../../../utils/validation'
import {colors} from '../../../utils/colors'
import {ReactSortable} from 'react-sortablejs'
import {isNewFeatureEnabled} from '../../../utils/newFeature'
import renderSkeletonLoaders from '../../../utils/loader'
import classNames from 'classnames'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'

const mobileWidth = 768

const StructureForm = ({id, statuspageData, isLimitExceeded, isDarkMode}) => {
    const dispatch = useDispatch()
    const text = useNamespaceTranslations('statuspages')
    const validationText = useNamespaceTranslations('validation')
    const resources = useSelector((state) => state.resources.resources)
    const teams = useSelector((state) => state.teams.teams)
    const isProcessing = useSelector((state) => state.statuspage.isProcessing)
    const [active, setActive] = useState(null)
    const [allResources, setAllResources] = useState([])
    const [allServices, setAllServices] = useState([])
    const [isReinitialized, setIsReinitialized] = useState(true)
    const serviceOptions = allServices?.map((service, index) => {
        return {
            value: service?.service_id,
            label: `${service?.name} (${
                teams?.find((team) => team?.team_id === service?.team_id)?.name
            })`
        }
    })

    const structures = statuspageData?.items?.map((item) => {
        const firstComponentOfGroup = item?.data?.components?.[0]
        return item?.type === 'group'
            ? {
                id: item?.data.id,
                group_name: item?.data?.name,
                component_name: firstComponentOfGroup?.name,
                description: firstComponentOfGroup?.description,
                status: text.statusOptions?.find(
                    (status) => status.value === firstComponentOfGroup?.status
                ),
                type: text.typeOptions?.find(
                    (type) => type.value === firstComponentOfGroup?.type
                ),
                service: {
                    label: `${allServices?.[0]?.name} (${
                        teams?.find((team) => team?.team_id === allServices?.[0]?.team_id)
                            ?.name
                    })`,
                    value: allServices?.[0]?.service_id
                },
                is_group: text.groupedOptions[0],
                components: item?.data?.components
                    ?.filter(
                        (component) => component?.type === text.typeOptions?.[0]?.value
                    )
                    ?.map((component) => {
                        return {
                            id: component?.id,
                            resource: allResources
                                ?.reduce((accumulator, currentArray) => {
                                    return accumulator.concat(currentArray?.resources)
                                }, [])
                                ?.find(
                                    (resource) =>
                                        resource?.resource_id === component?.resource_id
                                ),
                            type: text.typeOptions?.find(
                                (type) => type.value === component?.type
                            ),
                            status: text.statusOptions?.find(
                                (status) => status.value === component?.status
                            )
                        }
                    }),
                custom_components: item?.data?.components?.map((component) => {
                    return {
                        id: component?.id,
                        name: component?.name,
                        description: component?.description
                    }
                })
            }
            : {
                id: item?.data?.id,
                group_name: '',
                component_name: item?.data?.name,
                description: item?.data?.description,
                service: {
                    label: `${allServices?.[0]?.name} (${
                        teams?.find((team) => team?.team_id === allServices?.[0]?.team_id)
                            ?.name
                    })`,
                    value: allServices?.[0]?.service_id
                },
                is_group:
            item?.type === 'group'
                ? text.groupedOptions[0]
                : text.groupedOptions[1],
                resource: allResources
                    ?.reduce((accumulator, currentArray) => {
                        return accumulator.concat(currentArray?.resources)
                    }, [])
                    ?.find(
                        (resource) => resource?.resource_id === item?.data?.resource_id
                    ),
                type: text.typeOptions.find(
                    (type) => type.value === item?.data?.type
                ),
                components: item?.data?.resource_id
                    ? [
                        {
                            resource: allResources
                                ?.reduce((accumulator, currentArray) => {
                                    return accumulator.concat(currentArray?.resources)
                                }, [])
                                ?.find(
                                    (resource) =>
                                        resource?.resource_id === item?.data?.resource_id
                                )
                        }
                    ]
                    : [],
                custom_components: [
                    {
                        id: item?.data?.id,
                        name: item?.data?.name,
                        description: item?.data?.description
                    }
                ]
            }
    })

    const customComponentStructure = {
        id: '',
        name: '',
        description: ''
    }

    const structure = {
        id: '',
        group_name: '',
        component_name: '',
        description: '',
        type: text.typeOptions[0],
        service: {
            label: `${allServices?.[0]?.name} (${
                teams?.find((team) => team?.team_id === allServices?.[0]?.team_id)?.name
            })`,
            value: allServices?.[0]?.service_id
        },
        is_group: text.groupedOptions[0],
        components: [],
        custom_components: [customComponentStructure],
        resources: []
    }

    const initialFormValues = {
        structures: structures?.length > 0 ? structures : id ? [] : [structure]
    }

    const excludeItemsWithResourceIds = (list1, list2) => {
        const resourceIds = list2?.map((item) => item?.resource?.resource_id)

        return list1?.filter((item) => {
            return !resourceIds?.includes(item?.value)
        })
    }

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const handleReinitialize = () => {
        setIsReinitialized(false)
    }

    const getAllResources = async() => {
        try {
            const serviceResources = await dispatch(
                getAllServicesEnabledResources(allServices)
            )

            const resources = serviceResources.map(data => data.resources).flat(1)

            dispatch(setResources(resources))
            setAllResources(serviceResources)
        } catch {
            return
        }
    }

    const getAllServicess = async() => {
        setAllServices(await dispatch(getAllServices(teams)))
    }

    const updateOrder = (data, setFieldValue) => {
        setFieldValue(
            'structures',
            data?.map((item, index) => {
                return {
                    ...item,
                    order: index + 1
                }
            })
        )
    }

    const getContent = (values, handleChange, setFieldValue, errors, remove) => {
        return values.structures.map((structureItem, index) => {
            const isGroup = values.structures?.[index]?.is_group.value
            const resourcesOptions =
        structureItem?.resources?.length > 0
            ? structureItem?.resources
            : resources
                ?.filter((resource) => resource?.enabled && resource?.state &&
        (resource?.service_id === structureItem?.service?.value))
                ?.map((resource) => {
                    return {
                        value: resource?.resource_id,
                        label: resource?.name
                    }
                })

            return (
                <div key={index} className={classNames('box', {
                    dark: isDarkMode
                })}>
                    {isProcessing ? renderSkeletonLoaders(1) :
                        <CustomSelect
                            name={`${names.structures}.${index}.${names.is_group}`}
                            value={values.structures?.[index].is_group}
                            options={text.groupedOptions}
                            placeholder={text.group_placeholder}
                            infoText={text.group_info}
                            onChange={(name, data) => {
                                setFieldValue(name, data)
                                setFieldValue(
                                    `${names.structures}.${index}.${names.components}`,
                                    []
                                )
                                setFieldValue(
                                    `${names.structures}.${index}.${names.custom_components}`,
                                    [customComponentStructure]
                                )
                                setFieldValue(`${names.structures}.${index}.${names.id}`, '')
                                setFieldValue(
                                    `${names.structures}.${index}.${names.type}`,
                                    text.typeOptions?.[0]
                                )
                            }}
                            activeInfo={
                                active === `${names.structures}.${index}.${names.is_group}`
                            }
                            handleInfo={(state) =>
                                handleInfo(
                                    `${names.structures}.${index}.${names.is_group}`,
                                    state
                                )
                            }
                            isDisabled={isLimitExceeded && !id}
                        />}
                    {!isGroup ? (
                        <>
                            {isProcessing ? renderSkeletonLoaders(1) :
                                <CustomSelect
                                    name={`${names.structures}.${index}.${names.type}`}
                                    value={structureItem?.type}
                                    options={text.typeOptions}
                                    placeholder={text.type_placeholder}
                                    infoText={text.component_type_info}
                                    onChange={(name, data) => {
                                        setFieldValue(name, data)
                                        setFieldValue(
                                            `${names.structures}.${index}.${names.components}`,
                                            []
                                        )
                                        setFieldValue(
                                            `${names.structures}.${index}.${names.custom_components}`,
                                            [customComponentStructure]
                                        )
                                        setFieldValue(`${names.structures}.${index}.${names.id}`, '')
                                    }}
                                    activeInfo={
                                        active ===
                  `${isGroup}.${names.structures}.${index}.${names.type}`
                                    }
                                    handleInfo={(state) =>
                                        handleInfo(
                                            `${isGroup}.${names.structures}.${index}.${names.type}`,
                                            state
                                        )
                                    }
                                    isDisabled={isLimitExceeded && !id}
                                />}
                            {structureItem?.type?.value === text.typeOptions[0].value ? (
                                isProcessing ? renderSkeletonLoaders(2) :
                                    <>
                                        <CustomSelect
                                            name={`${names.structures}.${index}.${names.service}`}
                                            value={structureItem?.service}
                                            options={serviceOptions}
                                            placeholder={text.service}
                                            infoText={text.service_info}
                                            onChange={(name, data) => {
                                                setFieldValue(name, data)
                                                setFieldValue(
                                                    `${names.structures}.${index}.${names.resources}`,
                                                    allResources
                                                        ?.find((item) => item?.service_id === data?.value)
                                                        ?.resources?.filter(resource => resource?.enabled && resource?.state)
                                                        ?.map((resource) => {
                                                            return {
                                                                label: resource?.name,
                                                                value: resource?.resource_id
                                                            }
                                                        })
                                                )
                                            }}
                                            activeInfo={
                                                active ===
                      `${isGroup}.${names.structures}.${index}.${names.service}`
                                            }
                                            handleInfo={(state) =>
                                                handleInfo(
                                                    `${isGroup}.${names.structures}.${index}.${names.service}`,
                                                    state
                                                )
                                            }
                                            isDisabled={isLimitExceeded && !id}
                                        />
                                        <CustomSelect
                                            name={`${names.structures}.${index}.${names.resource}`}
                                            value={
                                                excludeItemsWithResourceIds(
                                                    resourcesOptions,
                                                    values?.structures
                                                        ?.filter((structure) => !structure?.is_group?.value)
                                                        ?.reduce((accumulator, currentArray) => {
                                                            return accumulator.concat(currentArray?.components)
                                                        }, [])
                                                )?.[0] || ''
                                            }
                                            options={excludeItemsWithResourceIds(
                                                resourcesOptions,
                                                values?.structures
                                                    ?.filter((structure) => !structure?.is_group?.value)
                                                    ?.reduce((accumulator, currentArray) => {
                                                        return accumulator.concat(currentArray?.components)
                                                    }, [])
                                            )}
                                            error={errors?.structures?.[index]?.components}
                                            placeholder={text.resource}
                                            infoText={text.resource_info}
                                            onChange={(name, data) => {
                                                setFieldValue(name, data)
                                                const newValues = {...values?.structures}
                                                const components = [...newValues[index].components]
                                                components.push({
                                                    id: '',
                                                    type: values?.structures[index].type.value,
                                                    status: values?.structures[index].status,
                                                    resource: allResources
                                                        ?.reduce((accumulator, currentArray) => {
                                                            return accumulator.concat(currentArray?.resources)
                                                        }, [])
                                                        ?.find((item) => item?.resource_id === data.value)
                                                })

                                                setFieldValue(
                                                    `${names.structures}.${index}.${names.components}`,
                                                    components
                                                )
                                            }}
                                            activeInfo={
                                                active ===
                      `${isGroup}.${names.structures}.${index}.${names.resource}`
                                            }
                                            handleInfo={(state) =>
                                                handleInfo(
                                                    `${isGroup}.${names.structures}.${index}.${names.resource}`,
                                                    state
                                                )
                                            }
                                            isDisabled={isLimitExceeded && !id}
                                        />
                                        {values?.structures[index]?.components?.map(
                                            (item, resourceIndex) => (
                                                <div key={resourceIndex}>
                                                    <Input
                                                        value={item?.resource?.name}
                                                        customIcon={
                                                            <span className="component-remover">
                                                                <TrashIcon
                                                                    stroke={colors.colorBgFilter}
                                                                    width={22}
                                                                    height={22}
                                                                />
                                                            </span>
                                                        }
                                                        isReadonly={true}
                                                        customIconOnclick={() => {
                                                            setFieldValue(
                                                                `${names.structures}.${index}.${names.components}`,
                                                                values?.structures?.[index]?.components?.filter(
                                                                    (component) =>
                                                                        component?.resource?.resource_id !==
                                  item?.resource?.resource_id
                                                                )
                                                            )
                                                        }}
                                                    />
                                                </div>
                                            )
                                        )}
                                    </>
                            ) : (
                                <FieldArray
                                    name={`${names.structures}.${index}.${names.custom_components}`}
                                >
                                    {({push, remove}) => {
                                        const customComponentsCount =
                      values?.structures?.[index]?.custom_components?.length
                                        return values?.structures?.[index]?.custom_components?.map(
                                            (customComp, compIndex) => {
                                                return (
                                                    <div
                                                        className={`custom-component-container ${
                                                            customComponentsCount > 1 ? 'multi' : 'single'
                                                        }`}
                                                    >
                                                        {isProcessing ? renderSkeletonLoaders(1) :
                                                            <Input
                                                                name={`${names.structures}.${index}.${names.custom_components}.${compIndex}.${names.name}`}
                                                                value={customComp?.name}
                                                                error={
                                                                    errors?.structures?.[index]
                                                                        ?.custom_components?.[compIndex]?.name
                                                                }
                                                                placeholder={text.component_name_placeholder}
                                                                onChange={handleChange}
                                                                inputIsDisabled={isLimitExceeded && !id}
                                                                customIcon={
                                                                    compIndex === 0 && (
                                                                        <span className="component-add-icon">
                                                                            <CirclePlusIcon />
                                                                        </span>
                                                                    )
                                                                }
                                                                customIconOnclick={() =>
                                                                    push(customComponentStructure)
                                                                }
                                                            />}
                                                        {isProcessing ? renderSkeletonLoaders(1, 'textarea-skeleton') :
                                                            <Textarea
                                                                type="text"
                                                                name={`${names.structures}.${index}.${names.custom_components}.${compIndex}.${names.description}`}
                                                                value={customComp?.description}
                                                                error={
                                                                    errors?.structures?.[index]
                                                                        ?.custom_components?.[compIndex]?.description
                                                                }
                                                                placeholder={text.description_placeholder}
                                                                onChange={handleChange}
                                                                isDisabled={isLimitExceeded && !id}
                                                                customIcon={
                                                                    customComponentsCount > 1 && (
                                                                        <span className="component-remover">
                                                                            <TrashIcon
                                                                                stroke={colors.colorBgFilter}
                                                                                width={22}
                                                                                height={22}
                                                                            />
                                                                        </span>
                                                                    )
                                                                }
                                                                customIconOnclick={() => remove(compIndex)}
                                                            />}
                                                    </div>
                                                )
                                            }
                                        )
                                    }}
                                </FieldArray>
                            )}
                        </>
                    ) : (isProcessing ? renderSkeletonLoaders(2) :
                        <>
                            <Input
                                name={`${names.structures}.${index}.${names.group_name}`}
                                value={structureItem?.group_name}
                                error={errors?.structures?.[index]?.group_name}
                                placeholder={text.group_name_placeholder}
                                infoText={text.group_name_info}
                                onChange={handleChange}
                                activeInfo={
                                    active ===
                  `${isGroup}.${names.structures}.${index}.${names.group_name}}`
                                }
                                handleInfo={(state) =>
                                    handleInfo(
                                        `${isGroup}.${names.structures}.${index}.${names.group_name}}`,
                                        state
                                    )
                                }
                                inputIsDisabled={isLimitExceeded && !id}
                            />
                            <CustomSelect
                                name={`${names.structures}.${index}.${names.type}`}
                                value={structureItem?.type}
                                options={text.typeOptions}
                                placeholder={text.type_placeholder}
                                infoText={text.component_type_info}
                                onChange={(name, data) => {
                                    setFieldValue(name, data)
                                    setFieldValue(
                                        `${names.structures}.${index}.${names.components}`,
                                        []
                                    )
                                    setFieldValue(
                                        `${names.structures}.${index}.${names.custom_components}`,
                                        [customComponentStructure]
                                    )
                                    setFieldValue(`${names.structures}.${index}.${names.id}`, '')
                                }}
                                activeInfo={
                                    active ===
                  `${isGroup}.${names.structures}.${index}.${names.type}`
                                }
                                handleInfo={(state) =>
                                    handleInfo(
                                        `${isGroup}.${names.structures}.${index}.${names.type}`,
                                        state
                                    )
                                }
                                isDisabled={isLimitExceeded && !id}
                            />
                            {structureItem?.type?.value === text.typeOptions[0].value ? (
                                <>
                                    {isProcessing ? renderSkeletonLoaders(2) :
                                        <>
                                            <CustomSelect
                                                name={`${names.structures}.${index}.${names.service}`}
                                                value={structureItem?.service}
                                                options={serviceOptions}
                                                placeholder={text.service}
                                                infoText={text.service_info}
                                                onChange={(name, data) => {
                                                    setFieldValue(name, data)
                                                    setFieldValue(
                                                        `${names.structures}.${index}.${names.resources}`,
                                                        allResources
                                                            ?.find((item) => item?.service_id === data?.value)
                                                            ?.resources?.filter(resource => resource?.enabled && resource?.state)
                                                            ?.map((resource) => {
                                                                return {
                                                                    label: resource?.name,
                                                                    value: resource?.resource_id
                                                                }
                                                            })
                                                    )
                                                }}
                                                activeInfo={
                                                    active ===
                      `${isGroup}.${names.structures}.${index}.${names.service}`
                                                }
                                                handleInfo={(state) =>
                                                    handleInfo(
                                                        `${isGroup}.${names.structures}.${index}.${names.service}`,
                                                        state
                                                    )
                                                }
                                                isDisabled={isLimitExceeded && !id}
                                            />
                                            <CustomSelect
                                                name={`${names.structures}.${index}.${names.resource}`}
                                                value={text.resource_form_fields}
                                                options={excludeItemsWithResourceIds(
                                                    resourcesOptions,
                                                    structureItem?.components
                                                )}
                                                error={errors?.structures?.[index]?.components}
                                                placeholder={text.resource}
                                                infoText={text.resource_info}
                                                onChange={(name, data) => {
                                                    setFieldValue(name, data)
                                                    const newValues = {...values?.structures}
                                                    const components = [...newValues[index].components]
                                                    components.push({
                                                        id: '',
                                                        type: values?.structures[index].type.value,
                                                        status: values?.structures[index].status,
                                                        resource: allResources
                                                            ?.reduce((accumulator, currentArray) => {
                                                                return accumulator.concat(currentArray?.resources)
                                                            }, [])
                                                            ?.find((item) => item?.resource_id === data.value)
                                                    })
                                                    setFieldValue(
                                                        `${names.structures}.${index}.${names.components}`,
                                                        components
                                                    )
                                                }}
                                                activeInfo={
                                                    active ===
                      `${isGroup}.${names.structures}.${index}.${names.resource}`
                                                }
                                                handleInfo={(state) =>
                                                    handleInfo(
                                                        `${isGroup}.${names.structures}.${index}.${names.resource}`,
                                                        state
                                                    )
                                                }
                                                isDisabled={isLimitExceeded && !id}
                                            />
                                        </>}
                                    {values?.structures[index]?.components?.map(
                                        (item, componentIndex) => (
                                            <div key={componentIndex}>
                                                {isProcessing ? renderSkeletonLoaders(1) :
                                                    <Input
                                                        value={item?.resource?.name}
                                                        customIcon={
                                                            <span className="component-remover">
                                                                <TrashIcon
                                                                    stroke={colors.colorBgFilter}
                                                                    width={22}
                                                                    height={22}
                                                                />
                                                            </span>
                                                        }
                                                        isReadonly={true}
                                                        customIconOnclick={() => {
                                                            setFieldValue(
                                                                `${names.structures}.${index}.${names.components}`,
                                                                values?.structures[index]?.components?.filter(
                                                                    (component) =>
                                                                        component?.resource?.resource_id !==
                                  item?.resource?.resource_id
                                                                )
                                                            )
                                                        }}
                                                    />}
                                            </div>
                                        )
                                    )}
                                </>
                            ) : (
                                <FieldArray
                                    name={`${names.structures}.${index}.${names.custom_components}`}
                                >
                                    {({push, remove}) => {
                                        const customComponentsCount =
                      values?.structures?.[index]?.custom_components?.length
                                        return values?.structures?.[index]?.custom_components?.map(
                                            (customComp, compIndex) => {
                                                return (
                                                    <div
                                                        className={`custom-component-container ${
                                                            customComponentsCount > 1 ? 'multi' : 'single'
                                                        }`}
                                                    >
                                                        {isProcessing ? renderSkeletonLoaders(1) :
                                                            <Input
                                                                name={`${names.structures}.${index}.${names.custom_components}.${compIndex}.${names.name}`}
                                                                value={customComp?.name}
                                                                error={
                                                                    errors?.structures?.[index]
                                                                        ?.custom_components?.[compIndex]?.name
                                                                }
                                                                placeholder={text.component_name_placeholder}
                                                                onChange={handleChange}
                                                                inputIsDisabled={isLimitExceeded && !id}
                                                                customIcon={
                                                                    compIndex === 0 && (
                                                                        <span className="component-add-icon">
                                                                            <CirclePlusIcon />
                                                                        </span>
                                                                    )
                                                                }
                                                                customIconOnclick={() =>
                                                                    push(customComponentStructure)
                                                                }
                                                            />}
                                                        {isProcessing ? renderSkeletonLoaders(1, 'textarea-skeleton') :
                                                            <Textarea
                                                                type="text"
                                                                name={`${names.structures}.${index}.${names.custom_components}.${compIndex}.${names.description}`}
                                                                value={customComp?.description}
                                                                error={
                                                                    errors?.structures?.[index]
                                                                        ?.custom_components?.[compIndex]?.description
                                                                }
                                                                placeholder={text.description_placeholder}
                                                                onChange={handleChange}
                                                                isDisabled={isLimitExceeded && !id}
                                                                customIcon={
                                                                    customComponentsCount > 1 && (
                                                                        <span className="component-remover">
                                                                            <TrashIcon
                                                                                stroke={colors.colorBgFilter}
                                                                                width={22}
                                                                                height={22}
                                                                            />
                                                                        </span>
                                                                    )
                                                                }
                                                                customIconOnclick={() => remove(compIndex)}
                                                            />}
                                                    </div>
                                                )
                                            }
                                        )
                                    }}
                                </FieldArray>
                            )}
                        </>
                    )}
                    {isNewFeatureEnabled() && (
                        <div className="box-dragger">
                            <DragIcon />
                        </div>
                    )}
                    {!isProcessing &&
                    <div onClick={() => remove(index)} className="box-remover">
                        <SecondaryTrashIcon stroke={colors.colorBgFilter} />
                    </div>}
                </div>
            )
        })
    }

    useEffect(() => {
        dispatch(getTeams())
    }, [])

    useEffect(() => {
        getAllServicess()
    }, [teams])

    useEffect(() => {
        getAllResources()
    }, [allServices])

    useEffect(() => {
        const formik = structureFormRef.current
        if (!formik) return
        formik.validateForm(formik.values).then((errs) => {
            formik.setErrors(errs || {})
        })
    }, [validationText])

    return (
        <div className="content structure">
            <Formik
                innerRef={structureFormRef}
                initialValues={initialFormValues}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={statuspageStructureSchema}
                enableReinitialize={isReinitialized}
                onSubmit
            >
                {({values, errors, handleChange, handleSubmit, setFieldValue}) => {
                    return (
                        <form onSubmit={handleSubmit} onClick={handleReinitialize}>
                            <FieldArray name={names.structures}>
                                {({push, remove}) => {
                                    return (
                                        <div className="form-elements">
                                            {isNewFeatureEnabled() ? (
                                                <ReactSortable
                                                    className="form-elements"
                                                    list={values.structures}
                                                    setList={(data) => {
                                                        updateOrder(data, setFieldValue)
                                                    }}
                                                    onStart={handleReinitialize}
                                                    filter={'.select-container'}
                                                    preventOnFilter={false}
                                                    handle={
                                                        mobileWidth >= window.innerWidth ? '.box-dragger' : ''
                                                    }
                                                >
                                                    {getContent(
                                                        values,
                                                        handleChange,
                                                        setFieldValue,
                                                        errors,
                                                        remove
                                                    )}
                                                </ReactSortable>
                                            ) : (
                                                getContent(
                                                    values,
                                                    handleChange,
                                                    setFieldValue,
                                                    errors,
                                                    remove
                                                )
                                            )}
                                            {isProcessing ? renderSkeletonLoaders(1, 'new-btn-skeleton') :
                                                !(isLimitExceeded && !id) && (
                                                    <ImgButton
                                                        type="button"
                                                        onClick={() => push(structure)}
                                                        className="new-btn light-bg"
                                                    >
                                                        <span className="button-text">{text.new}</span>
                                                        <PlusIcon stroke={colors.colorMuted} />
                                                    </ImgButton>
                                                )}
                                        </div>
                                    )
                                }}
                            </FieldArray>
                        </form>
                    )
                }}
            </Formik>
        </div>
    )
}

export default StructureForm
